import { ResourceType } from "@/enums";
import { AmiraResources } from "@/models";

export const getSkillResources = ({
  skillId,
  skillResources,
  lessonName,
  curriculumId,
  lessonSkills
}: {
  skillId: string;
  skillResources: { skillId: string; resources: AmiraResources[] }[];
  lessonName: string;
  curriculumId: string;
  lessonSkills: {skillId: string, displayName: string}[];
}) => {
  if (skillResources.length === 0 || skillId.length === 0) {
    return [];
  }
  const sharedTeacherResources: { [resourceUniqueId: string]: string[] } = {};
  for (const skill of skillResources) {
    if (!lessonSkills.some(s => s.skillId === skill.skillId)) {
      continue;
    }
    for (const resource of skill.resources) {
      if (resource.resourceType !== ResourceType.teacherResource) {
        continue;
      }
      if (!sharedTeacherResources[resource.resourceUniqueId]) {
        sharedTeacherResources[resource.resourceUniqueId] = [];
      }
      const displayName = lessonSkills.find(s => s.skillId === skill.skillId)?.displayName || "";
      if (!sharedTeacherResources[resource.resourceUniqueId].includes(displayName)) {
        sharedTeacherResources[resource.resourceUniqueId].push(displayName);
      }
    }
  }

  let resources: AmiraResources[] = [];
  if (
    skillResources.length &&
    skillId &&
    skillResources.find(s => s.skillId === skillId)
  ) {
    resources =
      skillResources.find(s => s.skillId === skillId)?.resources || [];
  }
  if (resources.length > 0) {
    // filter resources by tags related to the lesson
    resources = resources.filter(r => {
      
      // This section determines all possible resources that are assignable, including what's already assigned
      // Return all resources tagged with SKILL-${skillId}
      // EXCEPT ones with LESSONNAME-<something_else>[_DEFAULT]
      // unless they ALSO have the current lesson's LESSONNAME or CURR-... or SKILLDEFAULT tag
    
      const skillTag = `SKILL-${skillId}`;
      const skillDefaultTag = `SKILLDEFAULT-ASSIGNED`;
      const curriculumTag = `CURR-${curriculumId}`; // This will include both `CURR-${curriculumId}` and `CURR-${curriculumId}_DEFAULT`
      const lessonTag = `LESSONNAME-${lessonName}`;
      const lessonDefaultTag = `${lessonTag}_DEFAULT`;
    
      // Only include if it has the skill tag
      if (!r.tags.includes(skillTag)) {
        return false;
      }
    
      // Check for any other lesson tags
      const hasOtherLessonTag = r.tags.some(tag =>
        tag.startsWith("LESSONNAME-") &&
        tag !== lessonTag &&
        tag !== lessonDefaultTag
      );
    
      // Check if it has the current lesson tag
      const hasCurrentLessonTag =
        r.tags.includes(lessonTag) || r.tags.includes(lessonDefaultTag);

      // Check if it has the current curriculum tag
      const hasCurrentCurriculumTag = r.tags.includes(curriculumTag);

      // Check if it has the current skill default tag
      const hasCurrentSkillDefaultTag = r.tags.includes(skillDefaultTag);
    
      if (hasOtherLessonTag && !hasCurrentLessonTag && !hasCurrentCurriculumTag && !hasCurrentSkillDefaultTag) {
        return false;
      }
    
      return true;
    }).map(r =>{
      if (sharedTeacherResources[r.resourceUniqueId] && sharedTeacherResources[r.resourceUniqueId].length > 1) {
        return {
          ...r,
          sharedSkills: sharedTeacherResources[r.resourceUniqueId],
        };
      }
      return {...r, sharedSkills: []};
    });
  }
  return resources;
};
